import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import _ from "lodash";

import { colors } from "theme/palette";
import { fieldOptionRetrieve, useCaching } from "utils/common";

import NoPrint from "components/NoPrint";
import Stack from "components/Stack";
import Tag from "components/Tag";
import Text from "components/Text";
import toast from "components/Toast";

import { IPackageDetailsField } from "entities/Checklist/sdk";
import EditButton from "entities/Field/components/Radiobuttonfield/EditButton";
import { fieldValueCreate } from "entities/Field/sdk";
import FieldTitle from "entities/Memo/components/Fields/FieldTitle";
import { handleInvalidMemoFieldRequest } from "entities/Memo/components/Fields/utils/utils";
import { IFieldOption } from "entities/Package/sdk";

interface IRadiobuttonfield {
  field: IPackageDetailsField;
  dealId: number;
  onUpdate: () => void;
  isNASection: boolean;
  externalToken?: string;
}

const Radiobuttonfield = ({
  field,
  dealId,
  externalToken,
  isNASection,
  onUpdate,
}: IRadiobuttonfield) => {
  const [isOpen, setIsOpen] = useState(false);

  const [value, setValue] = useCaching<IFieldOption | null>(
    fieldOptionRetrieve({
      options: field.options,
      value: _.get(field.values, "[0].selected_value"),
    })
  );

  const navigate = useNavigate();

  const handleEditButtonClick = useCallback(() => setIsOpen(true), [setIsOpen]);

  const handleSave = useCallback(
    ({ newValue }: { newValue: IFieldOption | null }) => {
      setValue(newValue);
      fieldValueCreate({
        dealId,
        companyCustomFieldId: field.company_custom_field_id,
        value: newValue?.value || null,
      })
        .then(() => {
          setIsOpen(false);
          onUpdate();
          toast.successMessage(`${field.name} successfully updated!`);
        })
        .catch((error) =>
          handleInvalidMemoFieldRequest({ error, dealId, navigate })
        );
    },
    [field, dealId, setValue, onUpdate, navigate]
  );

  const handleCancel = useCallback(() => {
    setValue(
      fieldOptionRetrieve({
        options: field.options,
        value: _.get(field.values, "[0].selected_value"),
      })
    );
    setIsOpen(false);
  }, [setValue, field]);

  const isNAField = useMemo(
    () => field.n_a_status || isNASection,
    [field, isNASection]
  );

  const showMissingTag = useMemo(
    () => !isNAField && !value,
    [isNAField, value]
  );

  const showFieldValue = useMemo(() => !isNAField && value, [isNAField, value]);

  const showEditButton = useMemo(
    () => !isNAField && _.isUndefined(externalToken),
    [isNAField, externalToken]
  );

  return (
    <Stack direction="row" sx={{ borderBottom: `1px solid ${colors.blue40}` }}>
      <FieldTitle
        field={field}
        onUpdate={onUpdate}
        externalToken={externalToken}
        disabled={isNAField}
      />

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ flex: 1 }}
      >
        {showMissingTag && (
          <NoPrint>
            <Tag label="missing" variant="red" dataTestid="empty-field-value" />
          </NoPrint>
        )}
        {showFieldValue && (
          <Text variant="text2" data-testid="field-value">
            {value?.label}
          </Text>
        )}
        {isNAField && (
          <Text sx={{ opacity: 0.5 }} data-testid="field-value-na">
            N/A
          </Text>
        )}
        {showEditButton && (
          <NoPrint>
            <EditButton
              isOpen={isOpen}
              name={field.name}
              options={field.options}
              initialValue={value}
              onSave={handleSave}
              onCancel={handleCancel}
              onClick={handleEditButtonClick}
              buttonDataTestid="field-edit-button"
            />
          </NoPrint>
        )}
      </Stack>
    </Stack>
  );
};

export default Radiobuttonfield;
